import React, { useEffect, useState } from "react";
import api from "../api";
import { hasPermission } from "../utils/auth";
import { Link } from "react-router-dom";

export default function DashboardPage({ user }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [reqError, setReqError] = useState("");
  const [rejectNote, setRejectNote] = useState({});
  const canApproveAccess = hasPermission(user, "access-requests:approve");
  const canUpload = hasPermission(user, "contracts:create");

  const load = async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load dashboard");
    }
  };

  const loadRequests = async () => {
    if (!canApproveAccess) return;
    try {
      const res = await api.get("/contracts/access-requests");
      setRequests(Array.isArray(res.data) ? res.data : []);
      setReqError("");
    } catch (err) {
      console.error("Access requests error:", err);
      setReqError(err.response?.data?.error || "Failed to load access requests");
    }
  };

  useEffect(() => {
    load();
    loadRequests();
  }, [user]);

  const statCard = (title, value, accent = "bg-navy") => (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="text-2xl font-bold text-navy mt-1">{value}</div>
    </div>
  );

  const actOnRequest = async (reqId, contractId, action) => {
    try {
      let body = {};
      if (action === "reject") {
        const note =
          rejectNote[reqId] ??
          window.prompt("Please provide a reason for rejection (required)", "");
        if (note === null) {
          return; // cancel: leave in list
        }
        const trimmed = note.trim();
        if (!trimmed) {
          setReqError("Rejection reason is required.");
          return;
        }
        body = { reason: trimmed };
        setRejectNote((prev) => ({ ...prev, [reqId]: trimmed }));
      }
      await api.post(
        `/contracts/${contractId}/requests/${reqId}/${action}`,
        body
      );
      await loadRequests();
    } catch (err) {
      console.error("Update request error:", err);
      setReqError(err.response?.data?.error || "Failed to update request");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">
            Overview of contracts, processing, and users.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "CEO" && (
            <a
              href={`${
                process.env.REACT_APP_CORE_IDENTITY_URL ||
                "https://masterdashboard.xpresspaper.eu"
              }/dashboard.html`}
              className="text-xs text-navy font-semibold underline"
              target="_blank"
              rel="noreferrer"
            >
              Manage permissions in Core
            </a>
          )}
          {canUpload && (
          <div className="flex gap-2">
            <Link
              to="/contracts/new"
              className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90"
            >
              Upload Contract
            </Link>
          </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCard("Total Contracts", data?.contracts?.total ?? 0)}
        {statCard("Ready", data?.contracts?.ready ?? 0)}
        {statCard("Processing", data?.contracts?.processing ?? 0)}
        {statCard("Failed", data?.contracts?.failed ?? 0)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">
              Recent Contracts
            </div>
            <Link to="/contracts" className="text-xs text-navy font-semibold">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(data?.recentContracts || []).map((c) => (
              <div key={c.id} className="px-4 py-3 text-sm flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{c.title}</div>
                  <div className="text-xs text-slate-500">
                    {c.status} • {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/contracts/${c.id}/chat`}
                    className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Chat
                  </Link>
                  {user?.role === "CEO" && (
                    <Link
                      to="/contracts"
                      className="text-xs px-3 py-1 rounded border border-navy text-navy hover:bg-navy/10"
                    >
                      Manage
                    </Link>
                  )}
                </div>
              </div>
            ))}
            {(!data?.recentContracts || data.recentContracts.length === 0) && (
              <div className="px-4 py-4 text-xs text-slate-500">
                No recent contracts.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">
              Failed / Issues
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {(data?.recentFailed || []).map((f) => (
              <div key={f.id} className="px-4 py-3 text-sm">
                <div className="font-semibold text-red-700">{f.title}</div>
                <div className="text-xs text-red-600">
                  {f.error_message || "Failed"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {new Date(f.updated_at).toLocaleString()}
                </div>
              </div>
            ))}
            {(!data?.recentFailed || data.recentFailed.length === 0) && (
              <div className="px-4 py-4 text-xs text-slate-500">
                No failures reported.
              </div>
            )}
          </div>
        </div>

        {canApproveAccess && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">
                Access Requests
              </div>
              <span className="text-xs text-slate-500">
                Pending approvals
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {reqError && (
                <div className="px-4 py-3 text-xs text-red-600 bg-red-50">
                  {reqError}
                </div>
              )}
              {requests.length === 0 && !reqError && (
                <div className="px-4 py-4 text-xs text-slate-500">
                  No pending requests.
                </div>
              )}
              {requests.map((r) => (
                <div key={r.id} className="px-4 py-3 text-sm flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {r.requester_name || r.employee_id || "Unknown requester"}
                    </div>
                    <div className="text-xs text-slate-600">
                      {(r.requester_email || r.employee_id || "No email")} • {r.contract_title}
                    </div>
                    {r.reason && (
                      <div className="text-xs text-slate-500 mt-1">
                        Reason: {r.reason}
                      </div>
                    )}
                    {r.decision_reason && (
                      <div className="text-xs text-red-600 mt-1">
                        Rejection note: {r.decision_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => actOnRequest(r.id, r.contract_id, "reject")}
                      className="text-xs px-3 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => actOnRequest(r.id, r.contract_id, "approve")}
                      className="text-xs px-3 py-1 rounded border border-green-200 text-green-700 hover:bg-green-50"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
